<template>
  <div class="varsling-form">
    <div
      v-if="sent"
      ref="success"
      class="alert alert-success"
      role="status"
      tabindex="-1"
    >
      {{ trans('varsling.success') }}
    </div>

    <form
      v-else
      novalidate
      @submit.prevent="submit"
    >
      <div class="form-group py-2">
        <label
          for="varsling-name"
          class="form-label"
        >
          {{ trans('varsling.fields.name') }}
          <span class="text-muted">({{ trans('varsling.optional') }})</span>
        </label>
        <input
          id="varsling-name"
          v-model="form.name"
          type="text"
          class="form-control"
          :class="{ 'is-invalid': errors.name }"
          :aria-invalid="errors.name ? 'true' : null"
          :aria-describedby="errors.name ? 'varsling-name-error' : null"
          maxlength="255"
          autocomplete="off"
          @input="clearError('name')"
        >
        <div
          v-if="errors.name"
          id="varsling-name-error"
          class="invalid-feedback"
        >
          {{ errors.name[0] }}
        </div>
      </div>

      <div class="form-group py-2">
        <label
          for="varsling-email"
          class="form-label"
        >
          {{ trans('varsling.fields.email') }}
          <span class="text-muted">({{ trans('varsling.optional') }})</span>
        </label>
        <input
          id="varsling-email"
          v-model="form.email"
          type="email"
          class="form-control"
          :class="{ 'is-invalid': errors.email }"
          :aria-invalid="errors.email ? 'true' : null"
          :aria-describedby="errors.email ? 'varsling-email-error' : null"
          maxlength="255"
          autocomplete="off"
          @input="clearError('email')"
        >
        <div
          v-if="errors.email"
          id="varsling-email-error"
          class="invalid-feedback"
        >
          {{ errors.email[0] }}
        </div>
      </div>

      <div class="form-group py-2">
        <label
          for="varsling-phone"
          class="form-label"
        >
          {{ trans('varsling.fields.phone') }}
          <span class="text-muted">({{ trans('varsling.optional') }})</span>
        </label>
        <input
          id="varsling-phone"
          v-model="form.phone"
          type="text"
          inputmode="tel"
          class="form-control"
          :class="{ 'is-invalid': errors.phone }"
          :aria-invalid="errors.phone ? 'true' : null"
          :aria-describedby="errors.phone ? 'varsling-phone-error' : null"
          maxlength="50"
          autocomplete="off"
          @input="clearError('phone')"
        >
        <div
          v-if="errors.phone"
          id="varsling-phone-error"
          class="invalid-feedback"
        >
          {{ errors.phone[0] }}
        </div>
      </div>

      <div class="form-group py-2">
        <label
          for="varsling-message"
          class="form-label"
        >{{ trans('varsling.fields.message') }} *</label>
        <textarea
          id="varsling-message"
          v-model="form.message"
          class="form-control"
          :class="{ 'is-invalid': errors.message }"
          :aria-invalid="errors.message ? 'true' : null"
          :aria-describedby="errors.message ? 'varsling-message-counter varsling-message-error' : 'varsling-message-counter'"
          rows="10"
          :maxlength="maxLength"
          required
          @input="clearError('message')"
        />
        <div
          id="varsling-message-counter"
          class="form-text text-end"
        >
          {{ form.message.length }} / {{ maxLength }}
        </div>
        <div
          v-if="errors.message"
          id="varsling-message-error"
          class="invalid-feedback d-block"
        >
          {{ errors.message[0] }}
        </div>
      </div>

      <div class="form-group py-2">
        <altcha-widget
          ref="altcha"
          :challenge="challengeUrl"
          :language="language"
          :configuration="altchaConfiguration"
          @statechange="onAltchaStateChange"
        />
        <div
          v-if="errors.altcha"
          id="varsling-altcha-error"
          class="invalid-feedback d-block"
        >
          {{ errors.altcha[0] }}
        </div>
      </div>

      <div
        v-if="failed"
        class="alert alert-danger"
        role="alert"
      >
        {{ trans('varsling.error') }}
      </div>

      <button
        type="submit"
        class="btn btn-primary"
        :disabled="sending"
      >
        {{ sending ? trans('varsling.sending') : trans('varsling.submit') }}
      </button>
    </form>
  </div>
</template>

<script>
const emptyForm = () => ({ name: '', email: '', phone: '', message: '' });

export default {
  name: 'VarslingFormComponent',
  props: {
    action: { type: String, required: true },
    challengeUrl: { type: String, required: true },
    maxLength: { type: Number, default: 10000 },
  },
  data() {
    return {
      form: emptyForm(),
      altchaPayload: null,
      errors: {},
      sending: false,
      sent: false,
      failed: false,
      language: document.documentElement.lang || 'no',
      // No interaction data is collected by the widget
      altchaConfiguration: JSON.stringify({ humanInteractionSignature: false }),
    };
  },
  mounted() {
    customElements.whenDefined('altcha-widget').then(() => {
      // Our texts on top of the widget's built-in English ones
      window.$altcha.i18n.set(this.language, {
        ...window.$altcha.i18n.get('en'),
        ...this.trans('varsling.altcha'),
      });
    });
  },
  methods: {
    onAltchaStateChange(event) {
      this.altchaPayload = event.detail.state === 'verified' ? event.detail.payload : null;
      if (this.altchaPayload) {
        this.clearError('altcha');
      }
    },

    clearError(field) {
      if (this.errors[field]) {
        const errors = { ...this.errors };
        delete errors[field];
        this.errors = errors;
      }
    },

    validate() {
      const errors = {};
      if (!this.form.message.trim()) {
        errors.message = [this.trans('varsling.validation.message_required')];
      }
      if (!this.altchaPayload) {
        errors.altcha = [this.trans('varsling.validation.captcha_required')];
      }
      return errors;
    },

    submit() {
      if (this.sending) {
        return;
      }
      this.failed = false;
      this.errors = this.validate();
      if (Object.keys(this.errors).length) {
        return;
      }

      this.sending = true;
      axios.post(this.action, { ...this.form, altcha: this.altchaPayload })
        .then((response) => {
          if (response.data && response.data.status === 'sent') {
            // The report does not stay in the page after it is sent
            this.form = emptyForm();
            this.altchaPayload = null;
            this.sent = true;
            this.$nextTick(() => {
              if (this.$refs.success) {
                this.$refs.success.focus();
              }
            });
          } else {
            this.fail();
          }
        })
        .catch((error) => {
          const response = error.response;
          if (response && response.status === 422 && response.data && response.data.errors) {
            this.errors = response.data.errors;
            if (this.errors.altcha) {
              this.resetAltcha();
            }
          } else {
            this.fail();
          }
        })
        .finally(() => {
          this.sending = false;
        });
    },

    fail() {
      // The entered text stays in the form. A solved captcha can be used only once.
      this.failed = true;
      this.resetAltcha();
    },

    resetAltcha() {
      this.altchaPayload = null;
      if (this.$refs.altcha && typeof this.$refs.altcha.reset === 'function') {
        this.$refs.altcha.reset();
      }
    },
  },
};
</script>
